import { useCountUp } from "./useCountUp";

const STATS = [
  {
    value: 120,
    suffix: "+",
    label: "Attack scenarios",
    spin: 9,
    orbit: 4.5,
    rings: "meridians",
  },
  {
    value: 99,
    suffix: ".9%",
    label: "Range uptime",
    spin: 14,
    orbit: 6,
    rings: "wide",
  },
  {
    value: 5,
    suffix: "",
    label: "Security frameworks",
    spin: 11,
    orbit: 5.4,
    reverse: true,
    rings: "core",
  },
  {
    value: 60,
    prefix: "<",
    suffix: "s",
    label: "Time to provision",
    spin: 13,
    orbit: 3.6,
    reverse: true,
    rings: "axis",
  },
];

const STROKE = "color-mix(in srgb, var(--color-primary) 55%, transparent)";

function OrbRings({ kind, spin, reverse }) {
  const common = { fill: "none", stroke: STROKE, strokeWidth: 1 };
  return (
    <svg
      viewBox="0 0 64 64"
      className="lp-orb-spin absolute inset-0 h-full w-full"
      style={{
        "--lp-orb-spin": `${spin}s`,
        animationDirection: reverse ? "reverse" : "normal",
      }}
    >
      <circle cx="32" cy="32" r="21" {...common} />
      {kind === "meridians" && (
        <>
          <ellipse cx="32" cy="32" rx="9" ry="21" {...common} />
          <ellipse cx="32" cy="32" rx="21" ry="9" {...common} />
          <path d="M11 32h42M32 11v42" {...common} />
        </>
      )}
      {kind === "wide" && (
        <>
          <ellipse cx="32" cy="32" rx="13" ry="21" {...common} />
          <ellipse cx="32" cy="32" rx="21" ry="13" {...common} />
        </>
      )}
      {kind === "core" && (
        <>
          <ellipse cx="32" cy="32" rx="7" ry="21" {...common} />
          <ellipse cx="32" cy="32" rx="21" ry="7" {...common} />
          <circle cx="32" cy="32" r="11" {...common} />
        </>
      )}
      {kind === "axis" && (
        <>
          <ellipse cx="32" cy="32" rx="11" ry="21" {...common} />
          <path d="M11 32h42" {...common} />
          <circle
            cx="32"
            cy="32"
            r="4"
            fill="color-mix(in srgb, var(--color-primary) 50%, transparent)"
            stroke="none"
          />
        </>
      )}
    </svg>
  );
}

function Stat({
  value,
  prefix = "",
  suffix = "",
  label,
  spin,
  orbit,
  reverse,
  rings,
}) {
  const [ref, current] = useCountUp(value);
  return (
    <div ref={ref} className="relative z-10 text-center">
      {/* orb */}
      <div className="relative mx-auto mb-4 h-16 w-16">
        <span
          className="lp-orb-glow absolute -inset-2 rounded-full"
          style={{
            background:
              "radial-gradient(circle, color-mix(in srgb, var(--color-primary) 28%, transparent), transparent 65%)",
          }}
        />
        <OrbRings kind={rings} spin={spin} reverse={reverse} />
        <span
          className="lp-orb-orbit absolute inset-0"
          style={{ "--lp-orb-orbit": `${orbit}s` }}
        >
          <i className="absolute -top-0.75 left-1/2 -ml-0.75 h-1.5 w-1.5 rounded-full bg-(--color-primary) shadow-[0_0_8px_var(--color-primary)]" />
        </span>
      </div>

      <div className="qv-num text-4xl sm:text-5xl text-(--color-text-primary)">
        {prefix}
        {current}
        <span className="text-(--color-primary)">{suffix}</span>
      </div>
      <div className="mt-2 text-[12px] uppercase tracking-[0.16em] text-(--color-text-muted)">
        {label}
      </div>
    </div>
  );
}

export default function StatsBand() {
  return (
    <section className="relative py-14">
      <div className="mx-auto max-w-350 px-5 md:px-10">
        <div className="lp-reveal lp-zoom relative grid grid-cols-2 gap-y-10 px-2 py-10 md:grid-cols-4">
          {/* telemetry wire + travelling packets (desktop only) */}
          <div className="pointer-events-none absolute inset-x-0 top-18 z-0 hidden md:block">
            <span
              className="absolute inset-x-0 top-0 h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent, var(--color-border-solid) 12%, var(--color-border-solid) 88%, transparent)",
              }}
            />
            <i className="lp-wire-packet absolute -top-0.5 h-1.25 w-1.25 rounded-full bg-(--color-primary) shadow-[0_0_10px_var(--color-primary)]" />
            <i
              className="lp-wire-packet absolute -top-0.5 h-1.25 w-1.25 rounded-full bg-(--color-primary) shadow-[0_0_10px_var(--color-primary)]"
              style={{ animationDelay: "2.5s" }}
            />
          </div>

          {STATS.map((stat) => (
            <Stat key={stat.label} {...stat} />
          ))}
        </div>
      </div>
    </section>
  );
}
